import { Activity, Bone, Layers, Laptop, PersonStanding, Hand, Zap } from "lucide-react";
import { FACTORS as F } from "./copy";

export const CONDITIONS = [
  {
    id: "back-pain",
    title: "Back and lower back pain",
    tile: "Back Pain",
    short: "Lower back ache, stiffness and recurring pain.",
    icon: Bone,
    overview:
      "Back pain, especially in the lower back, is one of the most common reasons people see a doctor. It can feel like a dull ache, stiffness, or a sharper pain that makes bending, sitting or standing uncomfortable. In many people it is linked to muscles, joints and everyday habits, and it often settles with the right guidance and activity.",
    factors: [F.posture, F.weakness, F.lifting, F.weight, F.injury, F.lifestyle],
    approach:
      "We begin by understanding your symptoms, work and daily routine, followed by a focused clinical examination. Your plan is conservative and personalised, and may include appropriate medication, exercises, rehabilitation, posture and ergonomic correction, and activity and lifestyle changes. Investigations are advised only when clinically needed.",
    when: "If your pain lasts more than a few weeks, keeps coming back, disturbs your sleep or daily activities, or spreads into a leg.",
  },
  {
    id: "neck-pain",
    title: "Neck and cervical pain",
    tile: "Neck Pain",
    short: "Neck stiffness and pain linked to screens and posture.",
    icon: Activity,
    overview:
      "Neck pain can show up as stiffness, an ache at the base of the skull or between the shoulder blades, or discomfort when turning the head. Long hours looking down at phones or screens, and sleeping or working in awkward positions, are common contributors.",
    factors: [F.posture, F.ergonomics, F.weakness, F.age, F.lifestyle],
    approach:
      "Assessment looks at your neck movement, posture, muscle strength and nerve function, along with your screen and work habits. Care is conservative and non-surgical, and may include appropriate medication, exercises, posture and ergonomic correction, and guidance on daily habits.",
    when: "If neck pain persists, comes with headaches, or is accompanied by pain, tingling, numbness or weakness in an arm or hand.",
  },
  {
    id: "sciatica",
    title: "Sciatica and radiating nerve pain",
    tile: "Sciatica",
    short: "Pain travelling from the back into the leg.",
    icon: Zap,
    overview:
      "Sciatica describes pain that travels from the lower back or buttock down the back of the leg, sometimes with tingling or numbness. It happens when a nerve root in the lower back becomes irritated. In many cases it can be managed without surgery, but the cause should always be assessed by a doctor.",
    factors: [F.posture, F.lifting, F.age, F.weight, F.movement],
    approach:
      "We examine your back and the nerves of the leg, and screen for warning signs. Care is conservative and non-surgical, and may include appropriate medication, exercises, rehabilitation and activity modification, adjusted as you improve. If your assessment suggests you need further investigation or a specialist opinion, we will explain why.",
    when: "If leg pain is severe, lasts more than a few weeks, or comes with numbness or weakness in the leg or foot.",
  },
  {
    id: "slip-disc",
    title: "Slip disc and disc-related problems",
    tile: "Slip Disc",
    short: "Disc-related back and neck problems.",
    icon: Layers,
    overview:
      "The discs between the vertebrae act as cushions. With age-related changes, strain or injury, a disc can bulge or herniate (often called a “slip disc”) and may press on a nearby nerve. Disc changes are also commonly seen on scans in people without any pain, so scan findings are always read together with your symptoms and examination.",
    factors: [F.age, F.lifting, F.posture, F.weight, F.injury, F.weakness],
    approach:
      "We treat the person, not just the scan. Your symptoms and clinical examination guide a conservative, non-surgical plan, which may include appropriate medication, exercises, rehabilitation and activity modification. Imaging is advised only when it is clinically needed.",
    when: "If you have back or neck pain that spreads into an arm or leg, or you have been told you have a disc problem and would like a conservative, non-surgical opinion.",
  },
  {
    id: "posture",
    title: "Posture-related pain",
    tile: "Posture Correction",
    short: "Aches linked to sitting, screens and daily habits.",
    icon: PersonStanding,
    overview:
      "Sitting or standing in the same position for long periods, slouching, or looking down at screens can load the muscles and joints of the spine unevenly. Over time this can lead to stiffness, aches and fatigue in the neck, upper back and lower back.",
    factors: [F.posture, F.ergonomics, F.weakness, F.movement, F.lifestyle],
    approach:
      "We assess how you sit, stand and move, and how your work and daily routine load your spine. Care focuses on posture correction, exercises to build strength and mobility, ergonomic changes, and practical habits you can keep.",
    when: "If posture-related aches are becoming frequent, are affecting your work, or are not settling with simple changes.",
  },
  {
    id: "shoulder-joint",
    title: "Shoulder, joint and musculoskeletal pain",
    tile: "Joint & Muscle Pain",
    short: "Shoulder, joint and muscle aches and strains.",
    icon: Hand,
    overview:
      "Pain in the shoulders, joints and muscles can come from strain, overuse, stiffness or reduced strength, and is often linked with posture and the way we move through the day.",
    factors: [F.lifting, F.weakness, F.injury, F.movement, F.age],
    approach:
      "We examine the painful area along with the posture and movement patterns that may be contributing. Your plan is conservative and personalised, and may include appropriate medication, exercises, rehabilitation and activity modification.",
    when: "If pain limits your movement, keeps returning, follows an injury, or comes with swelling, redness or fever.",
  },
  {
    id: "ergonomics",
    title: "Work-related musculoskeletal problems and ergonomics",
    tile: "Ergonomics",
    short: "Work-related strain and workstation set-up.",
    icon: Laptop,
    overview:
      "Desk work, long hours at a computer, driving, lifting and repetitive tasks can strain the spine, neck, shoulders and wrists. With his background in occupational health, Dr. Singh looks at how your work and workstation may be contributing to your symptoms.",
    factors: [F.posture, F.ergonomics, F.lifting, F.weakness, F.lifestyle],
    approach:
      "We look at your work tasks, workstation, sitting time and lifting habits. Care combines conservative treatment with an ergonomic assessment, practical workplace and workstation guidance, exercises, and movement breaks suited to your job.",
    when: "If work-related aches are recurring or affecting your productivity, or if you would like an ergonomic review of how you sit and work.",
  },
];

export const HOME_TILES = ["back-pain", "slip-disc", "sciatica", "posture", "ergonomics"].map((id) => CONDITIONS.find((c) => c.id === id));
